
import Vue from "vue";
import { Component } from "vue-property-decorator";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { fold } from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";
import { globalStore } from "@/store/store";
import { ElementPresetsV } from "@/types/presets";
import PresetsList from "./PresetsList.vue";
import AddPreset from "./AddPreset.vue";
import PanelHeader from "@/components/Reusable/Util/PanelHeader.vue";

// @ts-ignore
import draggable from "vuedraggable";
import BrowseModeToggle from "@/components/BrowseMode/BrowseModeToggle.vue";
import PanelHeaderButton from "@/components/Reusable/Util/PanelHeaderButton.vue";
import PresetsPanelHistoryViewMode from "@/components/Settings/ElementPresets/PresetsPanelHistoryViewMode.vue";
import AlertBox from "@/components/Reusable/CustomInputs/AlertBox.vue";
import FreeModeAlertForPresets from "@/components/Subscription/FreeModeAlertForPresets.vue";
import IconButton from "@/components/UI/IconButton.vue";
import NotificationLogger from "@/lib/notifications/notifications";
import fpJsonParseTypesafe from "@/util/fp-json-parse-typesafe";
import { getRandomString } from "@/util/random-string";
import NiceButton from "@/components/UI/NiceButton.vue";

@Component({
  components: {
    FreeModeAlertForPresets,
    AlertBox,
    PresetsPanelHistoryViewMode,
    PanelHeaderButton,
    BrowseModeToggle,
    draggable,
    PresetsList,
    AddPreset,
    PanelHeader,
    IconButton,
    NiceButton
  }
})
export default class ImportExportPresets extends Vue {
  openFileUpload() {
    const fileUpload = this.$refs.fileUpload as HTMLInputElement;
    fileUpload.click();
  }

  importPresets(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files && target.files[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;

      const newPresets = pipe(
        fpJsonParseTypesafe(text, ElementPresetsV),
        fold(
          () => null,
          els => els
        )
      );

      if (newPresets === null) {
        NotificationLogger.log.errorMessage(
          "The file you selected is not a valid Breakdance Presets file."
        );
        return;
      }

      if (!newPresets.length) {
        NotificationLogger.log.message(
          "The file you selected does not contain any presets."
        );
        return;
      }

      // Overwrite presets with the same IDs, then merge in the rest
      const newPresetsIds = newPresets.map(p => p.id);
      const unchangedPresets = globalStore.presets.filter(
        p => !newPresetsIds.includes(p.id)
      );

      globalStore.setPresets([...unchangedPresets, ...newPresets]);
    };

    reader.readAsText(file);

    // Clear the file input so that the user can select the same file again
    (event.target as HTMLInputElement).value = "";
  }

  exportPresets() {
    const filename = `breakdance-presets-${getRandomString()}.json`;

    const text = pipe(
      fpJsonStringify(globalStore.presets),
      fold(
        () => {
          return "";
        },
        (json: string) => {
          return json;
        }
      )
    );

    const element = document.createElement("a");
    const blob = new Blob([text], { type: "application/json" });

    element.setAttribute("href", window.URL.createObjectURL(blob));
    element.setAttribute("download", filename);

    element.style.display = "none";
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  }
}
